import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, ExternalLink, CheckCircle2, XCircle, Database, Server } from 'lucide-react';
import { fmMigration } from '../api';

/** Eigene Adresse des Werkzeugs; die Zugriffsliste des NPM sitzt davor. */
const FM_ADRESSE = 'https://fm-migration.juni.com';

/**
 * Das FileMaker-Migrationswerkzeug — von hier aus.
 *
 * Es läuft als eigener Dienst auf coruscant und hat eine eigene Oberfläche.
 * Diese Seite ersetzt sie nicht, sondern beantwortet, was man wissen will,
 * *bevor* man sie öffnet: läuft das Ding, welche Kunden sind eingerichtet, und
 * wohin zeigt jeder davon.
 *
 * Vor allem das Letzte: seit dem zentralen Betrieb bedient ein Werkzeug viele
 * ojfm-Instanzen, und „in welche Instanz schreibt dieser Kunde eigentlich" ist
 * die Frage, bei der ein Irrtum teuer wird.
 */
export default function FmMigrationPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['fm-migration', 'zustand'],
    queryFn: fmMigration.zustand,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <ArrowLeftRight size={20} /> FileMaker-Migration
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Zieht Datensätze aus einer FileMaker-Datenbank und lädt sie über die REST-Schnittstelle
            in eine open.junixx-Instanz. Läuft einmal zentral auf coruscant.
          </p>
        </div>
        <a
          href={FM_ADRESSE}
          target="_blank"
          rel="noreferrer"
          title="In einem eigenen Tab öffnen"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Eigener Tab <ExternalLink size={14} />
        </a>
      </div>

      {isLoading ? (
        <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
      ) : !data?.erreichbar ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <p className="flex items-center gap-2 text-sm font-medium text-red-800">
            <XCircle size={16} /> Das Werkzeug antwortet nicht
          </p>
          <p className="mt-1 text-xs leading-snug text-red-700">{data?.grund}</p>
          <p className="mt-2 text-xs leading-snug text-red-700">
            Es läuft als <code className="font-mono">fm-migration-backend</code> und{' '}
            <code className="font-mono">fm-migration-frontend</code> auf coruscant. Neu ausrollen:{' '}
            <code className="font-mono">/Container/fm-migration/deploy.sh</code>
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-surface p-5">
            <p className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 size={16} /> Läuft
            </p>
            <p className="mt-1 text-xs leading-snug text-slate-400">
              Der PC erreicht das Werkzeug direkt im Container-Netz — von außen liegt eine
              Zugangsliste davor, weil es selbst keine Anmeldung kennt.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-surface p-5">
            <h2 className="text-sm font-semibold text-slate-800">
              Eingerichtete Kunden{' '}
              <span className="font-normal text-slate-400">({data.kunden.length})</span>
            </h2>

            {data.kunden.length === 0 ? (
              <p className="mt-2 text-xs leading-snug text-slate-400">
                Noch keiner. Kunden werden im Werkzeug selbst angelegt — dort gehören auch die
                FileMaker-Zugangsdaten und das Migrationsziel hin.
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500">
                      <th className="pb-2 pr-4 text-left font-medium">Kunde</th>
                      <th className="pb-2 pr-4 text-left font-medium">FileMaker</th>
                      <th className="pb-2 text-left font-medium">Ziel (open.junixx)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.kunden.map((k) => (
                      <tr key={k.id}>
                        <td className="py-2 pr-4 font-medium text-slate-800">{k.name}</td>
                        <td className="py-2 pr-4">
                          <span className="flex items-center gap-1.5 font-mono text-xs text-slate-500">
                            <Server size={11} className="shrink-0" />
                            {k.fm_host}
                            <span className="text-slate-300">/</span>
                            {k.fm_database}
                          </span>
                        </td>
                        <td className="py-2">
                          {k.ojfm_url ? (
                            <span className="flex items-center gap-1.5 font-mono text-xs text-slate-500">
                              <Database size={11} className="shrink-0" />
                              {k.ojfm_url}
                            </span>
                          ) : (
                            // Ohne Ziel greift der Rückfall auf die Umgebung —
                            // und die ist im zentralen Betrieb leer. Ein Lauf
                            // würde also ins Leere schreiben statt in die
                            // Instanz dieses Kunden.
                            <span className="text-xs text-amber-700">
                              kein Ziel hinterlegt — ein Lauf ginge ins Leere
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* ══ DAS WERKZEUG SELBST: VERLINKT, NICHT MEHR EINGEBETTET ═════
              Bis 27.10.2026 lief es hier als iframe unter /fm/. Die Annahme
              war, dass dort die Anmeldung des PC greift — tat sie nicht: die
              steckt im Authorization-Header, und eine Iframe-Navigation sendet
              keinen. /fm/ war damit ohne Anmeldung offen, Kennwoerter
              inklusive. Der Pfad ist jetzt gesperrt (frontend/nginx.conf). */}
          <div className="rounded-xl border border-slate-200 bg-surface p-5">
            <a
              href={FM_ADRESSE}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Werkzeug öffnen <ExternalLink size={14} />
            </a>
            <p className="mt-2 text-xs leading-snug text-slate-400">
              Öffnet {FM_ADRESSE.replace('https://', '')} in einem eigenen Tab. Dort fragt der
              Proxy nach eigenen Zugangsdaten — die Anmeldung des PC gilt dort nicht.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
